/**
 * TemplateBank Component
 * 
 * This component displays a modal with available templates for creating new chapters.
 * It allows users to select a template and a text direction (LTR or RTL) for their chapter.
 * 
 * Features:
 * - Template selection with visual previews
 * - Direction selection (LTR/RTL) with automatic detection based on user language
 * - Real-time thumbnail updates based on selected direction
 * - Responsive design with scrollable content
 * - Internationalization support
 * 
 * @module TemplateBank
 */

import React, { useContext, useState, useEffect } from 'react';
import { I18nReactContext } from '../../../../../contexts/I18nContext';
import { v4 as uuidv4 } from 'uuid';
import CloseIcon from '@mui/icons-material/Close';
import FormatTextdirectionLToR from '@mui/icons-material/FormatTextdirectionLToR';
import FormatTextdirectionRToL from '@mui/icons-material/FormatTextdirectionRToL';
import './TemplateBank.scss';
import { templates } from '../../../lib/templates';
import { TemplateList } from './TemplateList';

/**
 * @typedef {import('../../../lib/templates').Template} Template
 */

/**
 * @typedef {Object} Chapter
 * @property {string} id - Unique identifier for the chapter
 * @property {string} title - Chapter title
 * @property {string} description - Chapter description
 * @property {string} template - Template type (e.g., 'advanced')
 * @property {import('../../../lib/templates').Component[]} components - Array of components in the chapter
 * @property {import('../../../lib/templates').BackgroundImage} [backgroundImage] - Background image settings
 */

/**
 * TemplateBank component displays a modal with available templates for creating new chapters.
 * Users can select a template to use as a starting point for their chapter content.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.onSelectTemplate - Callback function called when a template is selected, receives the new chapter object
 * @param {Function} props.onClose - Callback function called when the template bank is closed without selecting a template
 * @returns {JSX.Element} The rendered TemplateBank component
 */
export const TemplateBank = ({ onSelectTemplate, onClose }) => {
  // Get i18n context for translations and RTL status
  const { i18n, isRtl } = useContext(I18nReactContext);
  // State for the selected direction (default based on user language)
  const [direction, setDirection] = useState(isRtl ? 'rtl' : 'ltr');

  /**
   * Handles the selection of a template and creates a new chapter based on it
   * 
   * @param {Template} template - The selected template object
   */
  const handleSelectTemplate = (template) => {
    // Create a new chapter with the selected template
    const newChapter = {
      id: uuidv4(), // Generate a unique ID for the new chapter
      title: i18n.translate('weezbeeCourse.editor.title.defaultValue'),
      description: i18n.translate('weezbeeCourse.editor.description.defaultValue'),
      template: 'advanced', // All template-based chapters use the advanced editor
      components: template.getComponents(i18n, direction), // Generate components with translated content and direction
      backgroundImage: template.backgroundImage // Copy background image settings if present
    };

    // Call the provided callback with the new chapter
    onSelectTemplate(newChapter);
    // Close the template bank
    onClose();
  };

  /**
   * Handles the selection of a direction (LTR or RTL)
   * 
   * @param {string} newDirection - The selected direction ('ltr' or 'rtl')
   */
  const handleDirectionChange = (newDirection) => {
    setDirection(newDirection);
  };

  return (
    <div className="TemplateBank">
      {/* Header section with title, description, and controls */}
      <div className="template-bank-header">
        <div className="header-content">
          <h2>{i18n.translate('weezbeeCourse.templateBank.title')}</h2>
          <p>{i18n.translate('weezbeeCourse.templateBank.description')}</p>
        </div>

        {/* Controls in the top-right corner */}
        <div className="header-controls">
          {/* Direction selection */}
          <div className="direction-control">
            <span className="direction-label">{i18n.translate('weezbeeCourse.templateBank.directionSelection')}</span>
            <div className="direction-buttons">
              <button 
                className={`direction-button ${direction === 'ltr' ? 'active' : ''}`}
                onClick={() => handleDirectionChange('ltr')}
                aria-label={i18n.translate('weezbeeCourse.templateBank.ltrDirection')}
                title={i18n.translate('weezbeeCourse.templateBank.ltrDirection')}
              >
                <FormatTextdirectionLToR />
              </button>
              <button 
                className={`direction-button ${direction === 'rtl' ? 'active' : ''}`}
                onClick={() => handleDirectionChange('rtl')}
                aria-label={i18n.translate('weezbeeCourse.templateBank.rtlDirection')}
                title={i18n.translate('weezbeeCourse.templateBank.rtlDirection')}
              >
                <FormatTextdirectionRToL />
              </button>
            </div>
          </div>

          {/* Close button */}
          <button className="close-button" onClick={onClose} aria-label={i18n.translate('weezbeeCourse.templateBank.closeButton')}>
            <CloseIcon />
          </button>
        </div>
      </div>

      {/* Use the reusable TemplateList component */}
      <TemplateList 
        templates={templates} 
        direction={direction} 
        onSelectTemplate={handleSelectTemplate} 
      />
    </div>
  );
};
